'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { observer } from 'mobx-react';
import Image from 'next/image';
import {
  PiArrowLeftLight,
  PiPianoKeysFill,
} from 'react-icons/pi';
import { rightLanguage } from '@/sharedTypes/helpers';
import samplesSelectorModel from '../Samples/SamplesSelector/SamplesSelectorModel';
import { ButtonShadcn } from '../ui/button-shadcn';
import {TypographyH3 } from '../ui/typography';
import { NEXT_PUBLIC_SAMPLES_CDN_URL } from '@/shared/envs';
import { Spinner } from '../ui/spinner';
import { useTranslation } from 'react-i18next';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';
import MainFooter from './MainFooter';

type SampleDetailViewProps = {
  identifier: string;
};

const SampleDetailView = observer(({ identifier }: SampleDetailViewProps) => {
  const { t } = useTranslation();
  const lang = useLanguageFromPath();
  const router = useRouter();
  const [imageUrls, setImageUrls] = React.useState<string[]>([]);
  const [isLoadingImages, setIsLoadingImages] = React.useState(true);
  const [isLoadingSamples, setIsLoadingSamples] = React.useState(false);

  // Ensure samples are loaded
  React.useEffect(() => {
    const loadSamples = async () => {
      if (samplesSelectorModel.samples.length === 0) {
        setIsLoadingSamples(true);
        await samplesSelectorModel.refreshSamples();
        setIsLoadingSamples(false);
      }
    };

    loadSamples();
  }, []);

  // Find the sample from the loaded samples array instead of using selectedSample
  const sample = samplesSelectorModel.samples.find(
    (s) => s.identifier === identifier
  );

  // Generate all page URLs from webpPath
  React.useEffect(() => {
    if (!sample?.webpPath) {
      setIsLoadingImages(false);
      return;
    }

    const basePath = sample.webpPath.replace(/page-\d+\.webp$/, '');
    const cdnUrl = NEXT_PUBLIC_SAMPLES_CDN_URL || 'https://cdn.darpiano.com';

    // Try to load pages 1-10 (reasonable max for sheet music)
    const checkPages = async () => {
      const urls: string[] = [];

      for (let i = 1; i <= 10; i++) {
        const url = `${cdnUrl}/${basePath}page-${i}.webp`;
        try {
          const response = await fetch(url, { method: 'HEAD' });
          if (response.ok) {
            urls.push(url);
          } else {
            break; // Stop when we hit a 404
          }
        } catch {
          break; // Stop on error
        }
      }

      setImageUrls(urls);
      setIsLoadingImages(false);
    };

    checkPages();
  }, [sample?.webpPath]);

  if (isLoadingSamples) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Spinner className='size-8' />
      </div>
    );
  }

  if (!sample) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <p className='text-zinc-600'>{t('mainPage.sampleDetail.sheetNotFound')}</p>
          <button
            onClick={() => {
              const homePath = lang === 'ru' ? '/ru' : '/';
              router.push(homePath);
            }}
            className='mt-4 text-accent-blue hover:underline-offset-4'
          >
            {t('mainPage.sampleDetail.goBackToSheets')}
          </button>
        </div>
      </div>
    );
  }

  // Use lang to ensure re-render when language changes
  const title = sample.metadata.title?.[lang] || rightLanguage(sample.metadata.title) || t('mainPage.sampleDetail.untitled');
  const author = sample.metadata.author?.[lang] || rightLanguage(sample.metadata.author) || t('mainPage.sampleDetail.unknown');

  const handleDownloadPDF = async () => {
    // TODO: Implement PDF download
    console.log('Download PDF for:', identifier);
  };

  const handleShare = async () => {
    // TODO: Implement share functionality
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${title} - ${author}`,
          text: `Check out this piano piece: ${title} by ${author}`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert(t('mainPage.sampleDetail.linkCopied'));
    }
  };

  const handleStartPractice = () => {
    // Navigate to practice mode with correct language
    const practicePath = lang === 'ru' ? `/ru/practice?m=${identifier}` : `/practice?m=${identifier}`;
    router.push(practicePath);
  };

  const handleBackToGrid = () => {
    router.back();
  };

  return (
    <div className='h-full'>
      {/* Back button and title section */}
      <div className='mb-6 grid sm:grid-cols-[1fr_8fr_1fr]  items-center gap-4'>
        <ButtonShadcn
          onClick={handleBackToGrid}
          variant='ghost'
          size='icon'
          aria-label={t('mainPage.sampleDetail.backToSheets')}
          className='hidden sm:flex'
          data-testid='sample-detail-back-button'
        >
          <PiArrowLeftLight size={16} />
        </ButtonShadcn>
        <div className='text-center'>
          <TypographyH3>
            {author}:{' '}
            {title}
          </TypographyH3>
          {/* <p className='text-zinc-600 capitalize'>{author}</p> */}
        </div>
      </div>

      {/* Three Action Buttons */}
      <div className='mb-6 flex items-center justify-center gap-2'>
        {/* Download Button */}
        {/* <ButtonShadcn
          onClick={handleDownloadPDF}
          variant='outline'
          size='icon'
          aria-label={t('mainPage.sampleDetail.downloadPDF')}
        >
          <PiDownloadSimpleLight size={24} />
        </ButtonShadcn> */}

        {/* Practice Button (Piano Icon - Blue/Primary) */}
        <ButtonShadcn
          onClick={handleStartPractice}
          variant='outline'
          size='icon'
          aria-label={t('mainPage.sampleDetail.startPractice')}
          data-testid='sample-detail-practice-button'
        >
          <PiPianoKeysFill size={24} />
        </ButtonShadcn>

        {/* Share Button */}
        {/* <ButtonShadcn
          onClick={handleShare}
          variant='outline'
          size='icon'
          aria-label={t('mainPage.sampleDetail.share')}
        >
          <PiShareNetworkLight size={24} />
        </ButtonShadcn> */}
      </div>

      {/* Music Sheet Preview - Images */}
      <div className='w-full lg:w-3/4 justify-self-center bg-white border rounded-lg p-4'>
        <div className='flex flex-col'>
          {isLoadingImages ? (
            <div className='relative w-full aspect-[8.5/11] bg-zinc-50 rounded flex items-center justify-center'>
              <Spinner />
              {/* <p className='text-zinc-400 text-sm'>Loading sheet music...</p> */}
            </div>
          ) : imageUrls.length > 0 ? (
            imageUrls.map((url, index) => (
              <div key={index} className='w-full bg-white rounded'>
                <Image
                  src={url}
                  alt={`${title} by ${author} - Page ${index + 1}`}
                  width={1000}
                  height={1294}
                  className='w-full h-auto'
                  unoptimized
                  priority={index === 0}
                />
              </div>
            ))
          ) : (
            <div className='relative w-full aspect-[8.5/11] bg-zinc-100 rounded flex items-center justify-center'>
              <p className='text-zinc-400 text-sm'>{t('mainPage.sampleDetail.noPreviewAvailable')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default SampleDetailView;
